import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  Clock,
  Filter,
} from 'lucide-react'
import { useAgentStore } from '../store/useAgentStore'
import { fetcher, poster } from '../api/client'

interface Alert {
  id: number
  title: string
  description: string
  level: 'info' | 'warning' | 'critical' | 'emergency'
  category: string
  status: 'active' | 'acknowledged' | 'resolved'
  created_at: string
}

export default function AlertsPanel() {
  const { t } = useTranslation()
  const { selectedElderlyId } = useAgentStore()
  const queryClient = useQueryClient()
  const [filterLevel, setFilterLevel] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('active')

  const { data: alerts, isLoading } = useQuery<Alert[]>({
    queryKey: ['alerts', selectedElderlyId, filterLevel, filterStatus],
    queryFn: () =>
      fetcher(
        `/alerts?elderly_id=${selectedElderlyId || ''}&level=${
          filterLevel === 'all' ? '' : filterLevel
        }&status=${filterStatus}`
      ),
    enabled: !!selectedElderlyId,
    refetchInterval: 15000,
  })

  const acknowledgeMutation = useMutation({
    mutationFn: (alertId: number) => poster(`/alerts/${alertId}`, { status: 'acknowledged' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  })

  const resolveMutation = useMutation({
    mutationFn: (alertId: number) => poster(`/alerts/${alertId}`, { status: 'resolved' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  })

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'emergency':
        return <AlertTriangle size={24} className="text-danger-600" />
      case 'critical':
        return <AlertCircle size={24} className="text-danger-500" />
      case 'warning':
        return <AlertTriangle size={24} className="text-warning-600" />
      default:
        return <Info size={24} className="text-primary-500" />
    }
  }

  const getLevelBadge = (level: string) => {
    const styles = {
      emergency: 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400',
      critical: 'bg-danger-50 text-danger-600 dark:bg-danger-900/20 dark:text-danger-300',
      warning: 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400',
      info: 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
    }
    return styles[level as keyof typeof styles] || styles.info
  }

  if (!selectedElderlyId) {
    return (
      <div className="text-center py-20 text-gray-500 dark:text-gray-400 text-xl">
        {t('alerts.no_elderly_selected')}
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('alerts.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {t('alerts.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700">
          <Filter size={20} className="text-gray-400" />
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="bg-transparent border-none text-gray-700 dark:text-gray-300 focus:ring-0"
          >
            <option value="all">{t('alerts.all_levels')}</option>
            <option value="emergency">{t('alerts.emergency')}</option>
            <option value="critical">{t('alerts.critical')}</option>
            <option value="warning">{t('alerts.warning')}</option>
            <option value="info">{t('alerts.info')}</option>
          </select>
        </div>

        <div className="flex gap-2">
          {['active', 'acknowledged', 'resolved'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                filterStatus === status
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
              }`}
            >
              {t(`alerts.status_${status}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-12 text-xl">{t('common.loading')}</div>
        ) : alerts?.length === 0 ? (
          <div className="card text-center py-16">
            <CheckCircle size={64} className="mx-auto text-success-500 mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('alerts.all_clear')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400">{t('alerts.no_alerts')}</p>
          </div>
        ) : (
          alerts?.map((alert) => (
            <div
              key={alert.id}
              className="card flex items-start gap-4 hover:shadow-md transition-shadow"
            >
              <div className={`p-3 rounded-xl ${getLevelBadge(alert.level)}`}>
                {getLevelIcon(alert.level)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                    {alert.title}
                  </h4>
                  <span
                    className={`px-2 py-0.5 rounded-lg text-xs font-bold uppercase ${getLevelBadge(
                      alert.level
                    )}`}
                  >
                    {alert.level}
                  </span>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">{alert.description}</p>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Clock size={16} />
                  <span>{new Date(alert.created_at).toLocaleString()}</span>
                  <span className="mx-2">•</span>
                  <span className="capitalize">{alert.category}</span>
                </div>
              </div>

              <div className="flex gap-2">
                {alert.status === 'active' && (
                  <button
                    onClick={() => acknowledgeMutation.mutate(alert.id)}
                    className="px-4 py-2 rounded-xl bg-primary-50 text-primary-700 font-medium hover:bg-primary-100 transition-colors"
                  >
                    {t('alerts.acknowledge')}
                  </button>
                )}
                {alert.status !== 'resolved' && (
                  <button
                    onClick={() => resolveMutation.mutate(alert.id)}
                    className="px-4 py-2 rounded-xl bg-success-50 text-success-700 font-medium hover:bg-success-100 transition-colors"
                  >
                    {t('alerts.resolve')}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}